import moment from 'moment';
import Formatter from './Formatter';
import {
  ELEMENT_TYPE_MEASUREMENT, ELEMENT_TYPE_CALCULATION, ELEMENT_TYPE_DATE, DATE_TIME, DATE_FULL,
  ELEMENT_TYPE_TEXT, ELEMENT_TYPE_MULTIPLE_CHOICES, ELEMENT_TYPE_UNIQUE_CHOICE,
} from '../constants';
import ElementUtil from './ElementUtil';
import { toMoment } from './date';

const BOUNDS_ERROR = 'error:consistency-check.inconsistent-bounds';
const DECIMALS_ERROR = 'error:consistency-check.decimals-not-allowed';

export const checkSettingsValidity = (
  t, lowBound = '', highBound = '', integer = null, canBeEqual = false,
) => {
  // Check minimum and maximum compatibility
  const min = Number.parseFloat(lowBound);
  const max = Number.parseFloat(highBound);
  if (!Number.isNaN(min) && !Number.isNaN(max)) {
    if (!canBeEqual && (max <= min)) {
      throw new Error(t(BOUNDS_ERROR));
    }
    if (integer) {
      if (!(Number.isInteger(min) && Number.isInteger(max))) {
        throw new Error(t(DECIMALS_ERROR));
      }
    }
  }
  if (!Number.isNaN(min) || !Number.isNaN(max)) {
    const value = Number.isNaN(min) ? max : min;
    if (integer) {
      if (!Number.isInteger(value)) {
        throw new Error(t(DECIMALS_ERROR));
      }
    }
  }
};

export const checkDateValidity = (
  t, minDate = null, maxDate = null, isIncDate = false, isAVariable = false,
  canBeEqual = false,
) => {
  // Check if the date is valid
  const checkDate = (date) => {
    if (date && date instanceof moment) {
      if (!date.isValid()) {
        throw new Error(t('error:error.invalid-date'));
      }
    }
  };

  // Check if limit dates are consistent with each other and if they can be equal
  const checkDates = (min, max) => {
    if (min instanceof moment && max instanceof moment) {
      if (max.isBefore(min) || (!canBeEqual && max.isSame(min))) {
        throw new Error(t('error:consistency-check.inconsistent-dates'));
      }
    }
  };

  // Check if the other limits (inclusion date, a variable from the form) can be equal
  const checkEqualities = () => {
    if (!canBeEqual && (isIncDate || isAVariable)) {
      throw new Error(t('error:consistency-check.inconsistent-dates'));
    }
  };

  checkDate(minDate);
  checkDate(maxDate);
  if (minDate && maxDate) {
    checkDates(minDate, maxDate);
  }
  checkEqualities();
};

export const consistencyCheckActive = (element) => {
  switch (element.type) {
    case ELEMENT_TYPE_MEASUREMENT:
      return Boolean(
        !Number.isNaN(Number.parseFloat(element.min))
                || !Number.isNaN(Number.parseFloat(element.max))
                || element.integer,
      );

    case ELEMENT_TYPE_CALCULATION:
      return Boolean(
        !Number.isNaN(Number.parseFloat(element.min))
                || !Number.isNaN(Number.parseFloat(element.max)),
      );

    case ELEMENT_TYPE_DATE:
      return Boolean(
        element.before || element.after || element.less_than_inc_date
                || element.greater_than_inc_date || element.before_var || element.after_var,
      );

    case ELEMENT_TYPE_TEXT:
      return Boolean(
        (element.text_min_length !== undefined && element.text_min_length !== null)
                || (element.text_max_length !== undefined && element.text_max_length !== null)
                || (element.text_fixed_length !== undefined && element.text_fixed_length !== null)
                || element.text_allow_letters === false
                || element.text_allow_digits === false,
      );

    case ELEMENT_TYPE_UNIQUE_CHOICE:
    case ELEMENT_TYPE_MULTIPLE_CHOICES:
      if (element.implementable) {
        return Boolean(
          (element.list_min_items !== undefined && element.list_min_items !== null)
                    || (element.list_max_items !== undefined && element.list_max_items !== null)
                    // eslint-disable-next-line max-len
                    || (element.list_item_min_length !== undefined && element.list_item_min_length !== null)
                    // eslint-disable-next-line max-len
                    || (element.list_item_max_length !== undefined && element.list_item_max_length !== null)
                    // eslint-disable-next-line max-len
                    || (element.list_item_fixed_length !== undefined && element.list_item_fixed_length !== null)
                    || element.list_item_allow_letters === false
                    || element.list_item_allow_digits === false,
        );
      }
      return false;

    default:
      return false;
  }
};

export const formatDate = (format = DATE_FULL, value) => {
  const emptyComponent = undefined;
  const formatter = new Formatter(format, emptyComponent);
  const date = toMoment(value, format);
  return formatter.format(date);
};

export const checkTextSettingsValidity = (t, minLength = '', maxLength = '') => {
  const min = Number.parseInt(minLength, 10);
  const max = Number.parseInt(maxLength, 10);

  if (!Number.isNaN(min) && !Number.isNaN(max)) {
    if (max < min) {
      throw new Error(t('error:consistency-check.inconsistent-lengths'));
    }
    if (min < 0 || max < 0) {
      throw new Error(t('error:consistency-check.negative-length-not-allowed'));
    }
  }

  if (!Number.isNaN(min) && min < 0) {
    throw new Error(t('error:consistency-check.negative-length-not-allowed'));
  }

  if (!Number.isNaN(max) && max < 0) {
    throw new Error(t('error:consistency-check.negative-length-not-allowed'));
  }
};

export const checkListSettingsValidity = (
  t,
  minItems = '',
  maxItems = '',
  itemMinLength = '',
  itemMaxLength = '',
) => {
  const minItemsNum = Number.parseInt(minItems, 10);
  const maxItemsNum = Number.parseInt(maxItems, 10);

  if (!Number.isNaN(minItemsNum) && !Number.isNaN(maxItemsNum)) {
    if (maxItemsNum < minItemsNum) {
      throw new Error(t('error:consistency-check.inconsistent-item-counts'));
    }
    if (minItemsNum < 0 || maxItemsNum < 0) {
      throw new Error(t('error:consistency-check.negative-count-not-allowed'));
    }
  }

  const itemMinLen = Number.parseInt(itemMinLength, 10);
  const itemMaxLen = Number.parseInt(itemMaxLength, 10);

  if (!Number.isNaN(itemMinLen) && !Number.isNaN(itemMaxLen)) {
    if (itemMaxLen < itemMinLen) {
      throw new Error(t('error:consistency-check.inconsistent-item-lengths'));
    }
    if (itemMinLen < 0 || itemMaxLen < 0) {
      throw new Error(t('error:consistency-check.negative-length-not-allowed'));
    }
  }
};

const formatCodePattern = (regexPattern) => {
  if (!regexPattern || regexPattern.length === 0) return '';

  // Parse regex pattern to extract format characters
  let cleanRegex = regexPattern;
  if (cleanRegex.startsWith('^')) {
    cleanRegex = cleanRegex.substring(1);
  }
  if (cleanRegex.endsWith('$')) {
    cleanRegex = cleanRegex.substring(0, cleanRegex.length - 1);
  }

  // Replace regex patterns with format characters using delimiters
  const formatString = cleanRegex
    .replace(/\[A-Z\]/g, '|A|')
    .replace(/\[a-z\]/g, '|a|')
    .replace(/\[0-9\]/g, '|1|')
    .replace(/\\\//g, '|/|')
    .replace(/\//g, '|/|')
    .replace(/-/g, '|-|')
    .replace(/_/g, '|_|');

  // Extract format characters and return compact format (e.g., "AA-A-111")
  const formatChars = formatString.split('|').filter((char) => char !== '');
  return formatChars.join('');
};

export const getConsistencySettings = (
  element, projectElements = null, elements = null, pages = null, t, inclusion,
) => {
  const res = [];
  switch (element.type) {
    case ELEMENT_TYPE_MEASUREMENT: {
      const settings = {
        min: element.min,
        max: element.max,
        strict: element.strict,
        integer: element.integer,
      };
      Object.keys(settings).forEach((settingName) => {
        const value = settings[settingName];
        const setting = { text: '', value: null, comment: '' };
        if (value !== undefined) {
          if (settingName !== 'strict') {
            setting.text = t(`project:consistency-check.settings.${settingName}`);
            setting.value = value;
            res.push(setting);
          } else if (value) {
            setting.text = t(`project:consistency-check.settings.${settingName}`);
            res.push(setting);
          }
        }
      });
      return res;
    }

    case ELEMENT_TYPE_CALCULATION: {
      const { min, max } = element;
      if (min === max) {
        const setting = { text: '', value: null, comment: '' };
        setting.text = t('project:consistency-check.settings.specific-value');
        setting.value = min;
        res.push(setting);
      } else {
        const settings = {
          min: element.min,
          max: element.max,
          strict: element.strict,
        };
        Object.keys(settings).forEach((settingName) => {
          const value = settings[settingName];
          const setting = { text: '', value: null, comment: '' };
          if (value !== undefined) {
            if (settingName !== 'strict') {
              setting.text = t(`project:consistency-check.settings.${settingName}`);
              setting.value = value;
              res.push(setting);
            } else if (value) {
              setting.text = t(`project:consistency-check.settings.${settingName}`);
              res.push(setting);
            }
          }
        });
      }
      return res;
    }
    case ELEMENT_TYPE_DATE: {
      const { format } = element;
      const before = element.before ? formatDate(format, element.before) : undefined;
      const after = element.after ? formatDate(format, element.after) : undefined;
      const beforeVar = element.before_var;
      const afterVar = element.after_var;
      const lessThanIncDate = element.less_than_inc_date;
      const greaterThanIncDate = element.greater_than_inc_date;
      const customDate = before && after && before === after;
      const formDate = beforeVar && afterVar && beforeVar === afterVar;
      const specificDate = (customDate || (greaterThanIncDate && lessThanIncDate)
        || formDate);
      const castMinToDate = element.trunc_min === 'date';
      const castMaxToDate = element.trunc_max === 'date';

      const inclusionDate = `${t('project:consistency-check.inclusion-date')} ${inclusion ? `(${formatDate(false, inclusion.created_at)})` : ''}`;
      const pElements = projectElements.reduce((a, v) => ({ ...a, [v.id]: v }), {});

      const getComplexSetting = (pElementId, dateTypeKey, condition) => {
        const pElement = projectElements.find((pEl) => pEl.id === pElementId);
        if (!pElement) {
          return null;
        }
        const childrenElement = elements[pElement.element];
        const formattedName = ElementUtil.formatProjectElementName(
          pElement, pElements, elements, pages, t,
        );
        const setting = { text: '', value: null, comment: '' };
        setting.text = t(`project:consistency-check.settings.${dateTypeKey}-form-date`);
        setting.value = formattedName;
        if (format === DATE_TIME && childrenElement.format === DATE_TIME) {
          if (condition) {
            setting.comment = t(`project:consistency-check.settings.without-time${inclusion ? '-entry-mode' : ''}`);
          } else {
            setting.comment = t(`project:consistency-check.settings.with-time${inclusion ? '-entry-mode' : ''}`);
          }
        }
        return setting;
      };

      if (specificDate) {
        const setting = { text: '', value: null, comment: '' };
        if (customDate) {
          setting.value = before;
        } else if (formDate) {
          const settingFormDate = getComplexSetting(beforeVar, 'specific', castMinToDate && castMaxToDate);
          if (settingFormDate) {
            Object.assign(setting, settingFormDate);
          }
        } else if (greaterThanIncDate && lessThanIncDate) {
          setting.value = inclusionDate;
        }
        if (!formDate) {
          setting.text = t('project:consistency-check.settings.specific-form-date');
        }
        res.push(setting);
      } else {
        if (after || afterVar || greaterThanIncDate) {
          const setting = { text: '', value: null, comment: '' };
          if (after) {
            setting.value = after;
          } else if (afterVar) {
            const settingFormDate = getComplexSetting(afterVar, 'min', castMinToDate);
            Object.assign(setting, settingFormDate);
          } else if (greaterThanIncDate) {
            setting.value = inclusionDate;
          }
          if (!afterVar) {
            setting.text = t('project:consistency-check.settings.min-form-date');
          }
          res.push(setting);
        }
        if (before || beforeVar || lessThanIncDate) {
          const setting = { text: '', value: null, comment: '' };
          if (before) {
            setting.value = before;
          } else if (beforeVar) {
            const settingFormDate = getComplexSetting(beforeVar, 'max', castMaxToDate);
            Object.assign(setting, settingFormDate);
          } else if (lessThanIncDate) {
            setting.value = inclusionDate;
          }
          if (!beforeVar) {
            setting.text = t('project:consistency-check.settings.max-form-date');
          }
          res.push(setting);
        }
      }
      return res;
    }

    case ELEMENT_TYPE_TEXT: {
      // Longueur fixe
      if (element.text_fixed_length !== undefined && element.text_fixed_length !== null) {
        const setting = { text: '', value: null, comment: '' };
        setting.text = t('project:consistency-check.settings.text-fixed-length');
        setting.value = `${element.text_fixed_length} ${t('project:consistency-check.characters')}`;
        res.push(setting);

        // Format de code
        if (element.text_format_pattern && element.text_format_pattern.length > 0) {
          const formatSetting = { text: '', value: null, comment: '' };
          formatSetting.text = t('project:consistency-check.settings.text-format-pattern');
          formatSetting.value = formatCodePattern(element.text_format_pattern);
          res.push(formatSetting);
        }
      } else {
        // Longueur min/max
        if (element.text_min_length !== undefined && element.text_min_length !== null) {
          const setting = { text: '', value: null, comment: '' };
          setting.text = t('project:consistency-check.settings.text-min-length');
          setting.value = `${element.text_min_length} ${t('project:consistency-check.characters')}`;
          res.push(setting);
        }

        if (element.text_max_length !== undefined && element.text_max_length !== null) {
          const setting = { text: '', value: null, comment: '' };
          setting.text = t('project:consistency-check.settings.text-max-length');
          setting.value = `${element.text_max_length} ${t('project:consistency-check.characters')}`;
          res.push(setting);
        }

        // Options supplémentaires
        if (element.text_exclude_limits) {
          const setting = { text: '', value: null, comment: '' };
          setting.text = t('project:consistency-check.settings.text-exclude-limits');
          res.push(setting);
        }

        if (element.text_allow_letters === false) {
          const setting = { text: '', value: null, comment: '' };
          setting.text = t('project:consistency-check.settings.text-allow-letters');
          setting.comment = t('common:no');
          res.push(setting);
        }

        if (element.text_allow_digits === false) {
          const setting = { text: '', value: null, comment: '' };
          setting.text = t('project:consistency-check.settings.text-allow-digits');
          setting.comment = t('common:no');
          res.push(setting);
        }
      }
      return res;
    }

    case ELEMENT_TYPE_UNIQUE_CHOICE:
    case ELEMENT_TYPE_MULTIPLE_CHOICES: {
      if (!element.implementable) {
        return res;
      }

      // Longueur fixe
      if (element.list_item_fixed_length !== undefined && element.list_item_fixed_length !== null) {
        const setting = { text: '', value: null, comment: '' };
        setting.text = t('project:consistency-check.settings.list-item-fixed-length');
        setting.value = `${element.list_item_fixed_length} ${t('project:consistency-check.characters')}`;
        res.push(setting);

        // Format de code
        if (element.list_item_format_pattern && element.list_item_format_pattern.length > 0) {
          const formatSetting = { text: '', value: null, comment: '' };
          formatSetting.text = t('project:consistency-check.settings.list-item-format-pattern');
          formatSetting.value = formatCodePattern(element.list_item_format_pattern);
          res.push(formatSetting);
        }
      } else {
        // Longueur min/max
        if (element.list_item_min_length !== undefined && element.list_item_min_length !== null) {
          const setting = { text: '', value: null, comment: '' };
          setting.text = t('project:consistency-check.settings.list-item-min-length');
          setting.value = `${element.list_item_min_length} ${t('project:consistency-check.characters')}`;
          res.push(setting);
        }

        if (element.list_item_max_length !== undefined && element.list_item_max_length !== null) {
          const setting = { text: '', value: null, comment: '' };
          setting.text = t('project:consistency-check.settings.list-item-max-length');
          setting.value = `${element.list_item_max_length} ${t('project:consistency-check.characters')}`;
          res.push(setting);
        }

        // Options supplémentaires
        if (element.list_item_exclude_limits) {
          const setting = { text: '', value: null, comment: '' };
          setting.text = t('project:consistency-check.settings.list-item-exclude-limits');
          res.push(setting);
        }

        if (element.list_item_allow_letters === false) {
          const setting = { text: '', value: null, comment: '' };
          setting.text = t('project:consistency-check.settings.list-item-allow-letters');
          setting.comment = t('common:no');
          res.push(setting);
        }

        if (element.list_item_allow_digits === false) {
          const setting = { text: '', value: null, comment: '' };
          setting.text = t('project:consistency-check.settings.list-item-allow-digits');
          setting.comment = t('common:no');
          res.push(setting);
        }
      }

      return res;
    }
    default: return [];
  }
};
